<template>
  <header class="bg-white border-b border-gray-200 sticky top-0 z-30">
    <div class="px-4 lg:px-6 py-4">
      <div class="flex items-center justify-between">
        <!-- Left Side -->
        <div class="flex items-center">
          <!-- Mobile Menu Toggle -->
          <button
            @click="$emit('toggleSidebar')"
            class="p-2 rounded-lg hover:bg-gray-100 lg:hidden"
          >
            <Bars3Icon class="w-6 h-6" />
          </button>
          
          <!-- Page Title -->
          <div class="ml-4 lg:ml-0">
            <h1 class="text-2xl font-semibold text-gray-900">{{ pageTitle }}</h1>
            <p class="text-sm text-gray-500">{{ currentDate }}</p>
          </div>
        </div>
        
        <!-- Right Side -->
        <div class="flex items-center space-x-4">
          <!-- Search -->
          <div class="hidden md:block">
            <div class="relative">
              <input
                type="text"
                placeholder="Search..."
                class="w-64 pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <MagnifyingGlassIcon class="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>
          
          <!-- Quick Actions -->
          <button
            @click="showQuickCall = true"
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg"
            title="Quick Call"
          >
            <PhoneIcon class="w-5 h-5" />
          </button>
          
          <button
            class="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg relative"
            title="Notifications"
          >
            <BellIcon class="w-5 h-5" />
            <span class="absolute top-1 right-1 w-2 h-2 bg-danger-500 rounded-full"></span>
          </button>
          
          <!-- User Menu -->
          <Menu as="div" class="relative">
            <MenuButton class="flex items-center p-2 rounded-lg hover:bg-gray-100">
              <div class="w-8 h-8 bg-gray-300 rounded-full"></div>
              <ChevronDownIcon class="ml-2 w-4 h-4 text-gray-500" />
            </MenuButton>
            
            <transition
              enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95"
              enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75"
              leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95"
            >
              <MenuItems class="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 focus:outline-none">
                <MenuItem v-slot="{ active }">
                  <a
                    href="/settings"
                    :class="[
                      'block px-4 py-2 text-sm text-gray-700',
                      active ? 'bg-gray-100' : ''
                    ]"
                  >
                    Settings
                  </a>
                </MenuItem>
                <MenuItem v-slot="{ active }">
                  <button
                    @click="handleLogout"
                    :class="[
                      'block w-full text-left px-4 py-2 text-sm text-gray-700',
                      active ? 'bg-gray-100' : ''
                    ]"
                  >
                    Logout
                  </button>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/vue'
import {
  Bars3Icon,
  MagnifyingGlassIcon,
  PhoneIcon,
  BellIcon,
  ChevronDownIcon
} from '@heroicons/vue/24/outline'
import dayjs from 'dayjs'

const emit = defineEmits(['toggleSidebar'])

const route = useRoute()
const authStore = useAuthStore()

const showQuickCall = ref(false)

const pageTitle = computed(() => {
  const titles = {
    '/dashboard': 'Dashboard',
    '/profiles': 'Elder Profiles',
    '/calls': 'Call History',
    '/schedule': 'Schedule',
    '/analytics': 'Analytics & Reports',
    '/alerts': 'Alerts',
    '/settings': 'Settings'
  }
  return titles[route.path] || 'ElderCare'
})

const currentDate = computed(() => {
  return dayjs().format('dddd, MMMM D, YYYY')
})

async function handleLogout() {
  await authStore.logout()
}
</script>